import React, {useMemo} from "react";
import {
	Accordion, AccordionDetails, AccordionSummary, Box, Chip, Table, TableBody, TableCell, TableHead,
	TableRow, Typography, useTheme
} from "@mui/material";
import {ExpandMore} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useQuery} from "@tanstack/react-query";
import dayjs from "dayjs";
import EmptyState from "../../../../util/component/EmptyState";
import CustomLoader from "../../../../util/component/CustomLoader";
import {formatCurrency, getLocaleByLang} from "../../utils";
import {LOCALES} from "../../config";
import {fetchAncillaryHistory} from "../services/http.services";
import {AncillaryOperation, IAncillaryHistoryRecord} from "./models";
import {formatDeskDiscount, strikethroughGross} from "./historyAmounts";

export interface IAncillaryHistorySectionProps {
	idRentalAgreement: string | null | undefined;
	/** Se false la sezione non viene renderizzata (grant SCOPE_RA_ANCILLARY_HISTORY assente). */
	canView: boolean;
}

const OperationChip = ({operation}: {operation: AncillaryOperation}) => {
	const {t} = useTranslation(['network']);
	const color = operation === 'ADD' ? 'success' : 'error';
	return (
		<Chip size={'small'} color={color} label={t(`raDetail.ancillaries.postSign.history.operation.${operation}`)}/>
	);
};

/**
 * Sezione "storico modifiche accessori" del dettaglio RA (LPP-22289 / SCFE-1, RDT §2.1(8),(11),
 * interfaccia (4)). Consuma GET /network/rental-agreement/{idRa}/ancillary-history (registro delta)
 * e mostra operazione, delta firmato, sconto, periodo, stato di settlement e la firma digit-sign.
 */
const AncillaryHistorySection = ({idRentalAgreement, canView}: IAncillaryHistorySectionProps) => {
	const theme = useTheme();
	const {t, i18n} = useTranslation(['network']);

	const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => getLocaleByLang(i18n.language), [i18n.language]);

	const {data, isLoading, isError} = useQuery<IAncillaryHistoryRecord[]>({
		queryKey: ['ntw_ancillary_history', idRentalAgreement],
		queryFn: () => fetchAncillaryHistory(idRentalAgreement as string),
		enabled: canView && !!idRentalAgreement
	});

	if (!canView) {
		return null;
	}

	const records = data ?? [];

	const formatDate = (value?: string | number | null): string => {
		if (value === undefined || value === null) return '-';
		// I campi data dello storico arrivano come epoch seconds (numerico); dayjs si aspetta epoch ms.
		const parsed = typeof value === 'number' ? dayjs.unix(value) : dayjs(value);
		return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : '-';
	};

	return (
		<Accordion data-testid={'ancillary-history-section'}>
			<AccordionSummary expandIcon={<ExpandMore/>}>
				<Typography variant={'bodyLarge'}>
					{t('raDetail.ancillaries.postSign.history.title')}
				</Typography>
			</AccordionSummary>

			<AccordionDetails>
				<CustomLoader loading={isLoading}/>

				{
					isError &&
					<EmptyState title={''} content={t('raDetail.ancillaries.postSign.history.error')}/>
				}

				{
					!isLoading && !isError && records.length === 0 &&
					<EmptyState title={''} content={t('raDetail.ancillaries.postSign.history.empty')}/>
				}

				{
					!isLoading && !isError && records.length > 0 &&
					<Box sx={{overflowX: 'auto'}}>
						<Table size={'small'} data-testid={'ancillary-history-table'}>
							<TableHead>
								<TableRow>
									<TableCell>{t('raDetail.ancillaries.postSign.history.column.operationDate')}</TableCell>
									<TableCell>{t('raDetail.ancillaries.postSign.history.column.operation')}</TableCell>
									<TableCell>{t('raDetail.ancillaries.postSign.history.column.ancillaryDescription')}</TableCell>
									<TableCell align={'right'}>{t('raDetail.ancillaries.postSign.history.column.units')}</TableCell>
									<TableCell align={'right'}>{t('raDetail.ancillaries.postSign.history.column.delta')}</TableCell>
									<TableCell align={'right'}>{t('raDetail.ancillaries.postSign.history.column.discount')}</TableCell>
									<TableCell>{t('raDetail.ancillaries.postSign.history.column.period')}</TableCell>
									<TableCell>{t('raDetail.ancillaries.postSign.history.column.settlement')}</TableCell>
									<TableCell>{t('raDetail.ancillaries.postSign.history.column.operator')}</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{
									records.map((rec: IAncillaryHistoryRecord, index: number) => (
										<TableRow key={`${rec.ancillaryCode}_${rec.operationDate}_${index}`}
												  data-testid={'ancillary-history-row'}>
											<TableCell>{formatDate(rec.operationDate)}</TableCell>
											<TableCell><OperationChip operation={rec.operation}/></TableCell>
											{/* LPP-21950: descrizione accessorio; ripiego sul codice sui record pre-LPP-23910 (descr null). */}
											<TableCell>{rec.ancillaryDescr ?? rec.ancillaryCode}</TableCell>
											{/*
											  R2 (LPP-21950): unita' movimentate dall'operazione. I record
											  scritti prima di R2 non le hanno (nessun backfill): '-'.
											*/}
											<TableCell align={'right'}>{rec.ancillaryUnits ?? '-'}</TableCell>
											{/*
											  F24 (LPP-21950) — T2.59: il lordo era esposto dall'API e mai reso.
											  Quando differisce dal netto lo si mostra barrato accanto ad esso,
											  come fa l'emissione con un accessorio scontato: senza il prezzo di
											  partenza l'operatore non puo' ricostruire lo sconto applicato.
											*/}
											<TableCell align={'right'}>
												{
													strikethroughGross(rec) !== null &&
													<Typography component={'span'} data-testid={'history-gross'}
																sx={{
																	textDecoration: 'line-through',
																	color: theme.palette.text.disabled,
																	mr: theme.spacing(0.5)
																}}>
														{formatCurrency(locale as LOCALES, strikethroughGross(rec))}
													</Typography>
												}
												{formatCurrency(locale as LOCALES, rec.deltaAmountTaxIncluded ?? null)}
											</TableCell>
											{/* T2.59: percentuale E importo dello sconto, come sul documento firmato. */}
											<TableCell align={'right'} data-testid={'history-discount'}>
												{/*
												  `locale as LOCALES` e' la convenzione del repo (stessa cosa
												  nelle celle degli importi): il tipo del componente e' l'unione
												  di letterali che duplica l'enum — difetto noto e fuori CR
												  (T2.60bis, `en-IN`).
												*/}
												{formatDeskDiscount(rec, locale as LOCALES, formatCurrency)}
											</TableCell>
											<TableCell>
												{formatDate(rec.pickUpDate)}
												<Typography component={'span'} sx={{mx: theme.spacing(0.5)}}>→</Typography>
												{formatDate(rec.dropOffDate)}
											</TableCell>
											<TableCell>
												{
													rec.settlementStatus
														? t(`raDetail.ancillaries.postSign.history.settlement.${rec.settlementStatus}`)
														: '-'
												}
											</TableCell>
											<TableCell>{rec.operator ?? '-'}</TableCell>
										</TableRow>
									))
								}
							</TableBody>
						</Table>
					</Box>
				}
			</AccordionDetails>
		</Accordion>
	);
};

export default AncillaryHistorySection;
